/**
 * "+ Add contact" on a property, and "+ Link property" on a contact.
 *
 * One component, because it is one row: `contact_property_links` carries the
 * role, the bill-to flag and quote approval whichever end you approach it
 * from. Building it twice would be two places for the two ends to disagree.
 *
 * The search is the list engine — the same POST /list the screens use — so
 * there is no second, subtly different way to find a record.
 */
import { useEffect, useState } from 'react'
import { emptyView } from '@rowted/shared'
import { SlideOver } from './overlays.js'
import { Button, Checkbox, SectionLabel } from './primitives.js'
import { Field } from './fields.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'

interface Candidate {
  id: string
  primary: string
  secondary: string
}

export interface LinkRecordProps {
  open: boolean
  onClose: () => void
  /** Which end is fixed: linking a contact to a property, or the reverse. */
  side: 'contact' | 'property'
  /** The id of the record already in hand. */
  anchorId: string
  /** Ids already linked, so the list does not offer them twice. */
  excludeIds: string[]
  onLinked: () => void
  /** Offered when the search finds nothing — create then link, in one go. */
  onCreateNew?: () => void
}

/** Common roles, so the field is a choice rather than free text most days. */
export const ROLE_SUGGESTIONS = [
  'Property manager', 'Owner', 'Accounts payable', 'On-site', 'Tenant', 'Board member',
]

export function LinkRecord({
  open, onClose, side, anchorId, excludeIds, onLinked, onCreateNew,
}: LinkRecordProps) {
  // Linking a contact to a property searches properties, and vice versa.
  const searchArea = side === 'contact' ? 'properties' : 'contacts'

  const [search, setSearch] = useState('')
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [chosen, setChosen] = useState<Candidate | null>(null)
  const [role, setRole] = useState(ROLE_SUGGESTIONS[0]!)
  const [isPrimary, setIsPrimary] = useState(false)
  const [isBillTo, setIsBillTo] = useState(false)
  const [approvesQuotes, setApprovesQuotes] = useState(false)
  const [accessNote, setAccessNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return undefined

    const timer = setTimeout(() => {
      request<{ items: Record<string, unknown>[] }>(`/${searchArea}/list`, {
        method: 'POST',
        body: {
          view: {
            ...emptyView(),
            fields: searchArea === 'contacts'
              ? ['id', 'name', 'email', 'accountName']
              : ['id', 'name', 'addressLine1', 'city'],
          },
          search: search.trim() || undefined,
          limit: 20,
        },
      })
        .then((result) => setCandidates(result.items
          .filter((row) => !excludeIds.includes(String(row.id)))
          .map((row) => ({
            id: String(row.id),
            primary: String(row.name ?? ''),
            secondary: searchArea === 'contacts'
              ? [row.accountName, row.email].filter(Boolean).join(' · ')
              : [row.addressLine1, row.city].filter(Boolean).join(', '),
          }))))
        .catch(() => setCandidates([]))
    }, 250)

    return () => clearTimeout(timer)
  }, [open, search, searchArea, excludeIds])

  function reset() {
    setSearch('')
    setChosen(null)
    setRole(ROLE_SUGGESTIONS[0]!)
    setIsPrimary(false)
    setIsBillTo(false)
    setApprovesQuotes(false)
    setAccessNote('')
    setError(null)
  }

  async function save() {
    if (!chosen) return
    setBusy(true)
    setError(null)
    try {
      // The endpoint hangs off the contact either way, because the link is a
      // property of the person's relationship to the place.
      const contactId = side === 'contact' ? anchorId : chosen.id
      const propertyId = side === 'contact' ? chosen.id : anchorId

      await request(`/contacts/${contactId}/links`, {
        method: 'POST',
        body: {
          propertyId,
          role,
          isPrimary,
          isBillTo,
          approvesQuotes,
          accessNote: accessNote.trim() || null,
        },
      })
      reset()
      onLinked()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be linked.')
    } finally {
      setBusy(false)
    }
  }

  const title = side === 'contact' ? 'Link a property' : 'Add a contact'
  const searchLabel = side === 'contact' ? 'Find a property' : 'Find a contact'

  return (
    <SlideOver
      open={open}
      onClose={() => { reset(); onClose() }}
      title={title}
      width={420}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button disabled={busy} onClick={() => { reset(); onClose() }}>Cancel</Button>
          <Button variant="primary" disabled={!chosen || busy} onClick={save}>
            {busy ? 'Linking…' : 'Link'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={searchLabel} value={search} onChange={setSearch}
          placeholder="Search by name" autoFocus />

        <div className="max-h-[240px] overflow-y-auto rounded-card border border-line">
          {candidates.length === 0 && (
            <div className="px-3 py-4 text-center">
              <p className="m-0 text-cell text-text-muted">
                {search ? 'Nothing matches that.' : 'Nothing left to link.'}
              </p>
              {onCreateNew && (
                <Button className="mt-2" onClick={onCreateNew}>
                  {side === 'contact' ? 'Add a new property' : 'Add a new contact'}
                </Button>
              )}
            </div>
          )}

          {candidates.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              onClick={() => setChosen(candidate)}
              className={cn(
                'flex w-full flex-col items-start gap-0 border-b border-line-soft px-3 py-2 text-left last:border-b-0',
                chosen?.id === candidate.id ? 'bg-accent-soft' : 'hover:bg-surface-alt',
              )}
            >
              <span className="text-cell font-semibold text-text">{candidate.primary}</span>
              {candidate.secondary && (
                <span className="text-sub text-text-muted">{candidate.secondary}</span>
              )}
            </button>
          ))}
        </div>

        {chosen && (
          <>
            <label className="block">
              <span className="mb-1 block text-sub text-text-muted">Role here</span>
              <input
                list="link-roles"
                value={role}
                onChange={(event) => setRole(event.target.value)}
                className="h-9 w-full rounded-field border border-line bg-surface px-2 text-cell text-text"
              />
              <datalist id="link-roles">
                {ROLE_SUGGESTIONS.map((option) => <option key={option} value={option} />)}
              </datalist>
            </label>

            <div>
              <SectionLabel className="mb-1.5 block">On this property they are</SectionLabel>
              <div className="flex flex-col gap-1.5">
                <Checkbox
                  checked={isPrimary}
                  onChange={(event) => setIsPrimary(event.target.checked)}
                  label="The primary contact"
                />
                <Checkbox
                  checked={isBillTo}
                  onChange={(event) => setIsBillTo(event.target.checked)}
                  label="Who invoices go to"
                />
                <Checkbox
                  checked={approvesQuotes}
                  onChange={(event) => setApprovesQuotes(event.target.checked)}
                  label="Allowed to approve quotes"
                />
              </div>
            </div>

            <div>
              <SectionLabel className="mb-1.5 block">Note for the crew</SectionLabel>
              <textarea
                value={accessNote}
                onChange={(event) => setAccessNote(event.target.value)}
                rows={2}
                placeholder="Call 20 min before arrival — he unlocks the north gate."
                className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text placeholder:text-text-faint"
              />
            </div>

            <p className="m-0 text-sub text-text-faint">
              They will start on this contact’s notification defaults. Change what they are
              told about here once the link exists.
            </p>
          </>
        )}

        {error && <p className="m-0 text-cell text-danger">{error}</p>}
      </div>
    </SlideOver>
  )
}
